import React, { useState, useEffect } from "react";
import { Eye, EyeOff } from "lucide-react";
import FormItem from "../../components/ui/FormItem";
import FormActions from "../ui/FormActions";
import FormGrid from "../../components/ui/FormGrid";

import FileUpload from "../common/fileupload/FileUpload";
import Dropdown from "../common/dropdown/Dropdown";

import PropTypes from "prop-types";


import {
  validateMobile,
  validateEmail,
  validateWebsite,
  validateNumberWithLimit
} from "../../utils/validation";
import {
  COUNTRY_OPTION_LABELS,
  DEFAULT_COUNTRY_CODE,
  DIAL_OPTION_LABELS,
  getCountryCodeFromDialLabel,
  getCountryCodeFromLabel,
  getCountryCodeFromMobileCountryCode,
  getCountryLabel,
  getCountryName,
  getDialCode,
  getDialLabel,
  resolveCountryCode,
  splitInternationalMobile,
} from "../../data/countries";

const BUSINESS_TYPE_OPTIONS = ["builder", "broker"];

export default function AgentForm({ initialData = {}, onSubmit, isLoading, onClose = () => {} }) {
  const getDefaultFormData = () => ({
    name: "",
    email: "",
    mobile: "",
    phone_country_code: DEFAULT_COUNTRY_CODE,
    country_code: DEFAULT_COUNTRY_CODE,
    pincode: "",
    password: "",
    industry: "",
    business_type: "",
    about_us: "",
    brochure: null,
    crm_webhook_url: "",
    primary_budget_buffer_percent: 10,
    secondary_budget_buffer_percent: 20,
  });

  const [formData, setFormData] = useState(getDefaultFormData());
  const [showPassword, setShowPassword] = useState(false);

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (initialData?.id !== undefined) {
      const country_code = resolveCountryCode(initialData);
      const { phone_country_code, national } = initialData.mobile_country_code
        ? {
            phone_country_code: getCountryCodeFromMobileCountryCode(
              initialData.mobile_country_code,
              country_code,
            ),
            national: String(initialData.mobile || "").replace(/\D/g, ""),
          }
        : splitInternationalMobile(initialData.mobile, country_code);
      setFormData({
        name: initialData.name || "",
        email: initialData.email || "",
        mobile: national,
        phone_country_code,
        country_code,
        pincode: initialData.pincode || "",
        password: "",
        brochure: initialData.brochure_url || null,
        about_us: initialData.about_us || "",
        industry: initialData.industry || "",
        business_type: initialData.business_type || "",
        crm_webhook_url: initialData.crm_webhook_url || "",
        primary_budget_buffer_percent:
          initialData.primary_budget_buffer_percent ?? 10,
        secondary_budget_buffer_percent:
          initialData.secondary_budget_buffer_percent ?? 20,
      });
      return;
    }
    setFormData(getDefaultFormData());
    setErrors({});
  }, [initialData]);

  const isEdit = initialData?.id !== undefined;

  const clearFieldError = (name) => {
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
  
    if (name === "brochure") {
      const file = files?.[0];
      if (!file) return;
  
      setFormData((prev) => ({
        ...prev,
        brochure: file,
      }));
  
      clearFieldError(name);
  
    } else if (name === "mobile") {
      const maxDigits = formData.phone_country_code === "IN" ? 10 : 15;
      if (!validateNumberWithLimit(maxDigits)(value)) return;

      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));

      clearFieldError(name);

    } else if (name === "pincode" && formData.country_code === "IN") {
      if (!validateNumberWithLimit(6)(value)) return;

      setFormData((prev) => ({
        ...prev,
        pincode: value,
      }));

      clearFieldError(name);

    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));

      clearFieldError(name);
    }
  };

  const handleBusinessTypeChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      business_type: value,
    }));

    clearFieldError("business_type");
  };

  const handleCountryChange = (label) => {
    const country_code = getCountryCodeFromLabel(label);

    setFormData((prev) => ({
      ...prev,
      country_code,
    }));

    clearFieldError("country_code");
  };

  const handlePhoneCountryChange = (label) => {
    const phone_country_code = getCountryCodeFromDialLabel(label);

    setFormData((prev) => ({
      ...prev,
      phone_country_code,
    }));

    clearFieldError("mobile");
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim())
      newErrors.name = "Client name is required";

    if (!formData.email.trim())
      newErrors.email = "Email is required";
    else if (!validateEmail(formData.email))
      newErrors.email = "Invalid email address";

    if (!formData.mobile.trim())
      newErrors.mobile = "Mobile is required";
    else if (formData.phone_country_code === "IN") {
      if (!validateMobile(formData.mobile))
        newErrors.mobile = "Invalid mobile number";
    } else if (!/^\d{6,15}$/.test(formData.mobile))
      newErrors.mobile = "Invalid mobile number";

    if (!formData.country_code)
      newErrors.country_code = "Country is required";

    if (formData.country_code === "IN") {
      if (!formData.pincode.trim())
        newErrors.pincode = "Pincode is required";
      else if (!/^\d{6}$/.test(formData.pincode))
        newErrors.pincode = "For India, 6 digits is mandatory";
    }

    if (!formData.industry.trim())
      newErrors.industry = "Industry is required";

    if (!formData.business_type)
      newErrors.business_type = "Business type is required";

    if (!isEdit && !formData.password)
      newErrors.password = "Password is required";

    if (formData.crm_webhook_url && !validateWebsite(formData.crm_webhook_url))
      newErrors.crm_webhook_url = "Invalid website URL";

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validate()) return;

    const formDataObj = new FormData();
    formDataObj.append("name", formData.name);
    formDataObj.append("email", formData.email);
    formDataObj.append(
      "mobile_country_code",
      `+${getDialCode(formData.phone_country_code)}`,
    );
    formDataObj.append("mobile", formData.mobile);
    formDataObj.append("country_code", formData.country_code);
    formDataObj.append("country", getCountryName(formData.country_code));
    formDataObj.append("pincode", formData.pincode);
    formDataObj.append("password", formData.password);
    formDataObj.append("industry", formData.industry);
    formDataObj.append("business_type", formData.business_type);
    formDataObj.append("about_us", formData.about_us);
    formDataObj.append("crm_webhook_url", formData.crm_webhook_url);
    formDataObj.append("primary_budget_buffer_percent", formData.primary_budget_buffer_percent);
    formDataObj.append("secondary_budget_buffer_percent", formData.secondary_budget_buffer_percent);

    if (formData.brochure instanceof File)
      formDataObj.append("brochure", formData.brochure);

    onSubmit(formDataObj);
  };

  const handleCancel = () => {
    setFormData(getDefaultFormData());
    setShowPassword(false);
    setErrors({});
    onClose?.();
  };

  const isIndia = formData.country_code === "IN";

  return (
    <form className="agent-form" onSubmit={handleSubmit} aria-label="Agent form">
      <FormGrid>
        <FormItem
          label="Client Name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          error={errors.name}
        />

        <FormItem
          label="Email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          required
          error={errors.email}
        />



        <FormItem
          label="Country"
          name="country_code"
          error={errors.country_code}
          required
        >
          <Dropdown
            name="country_code"
            options={COUNTRY_OPTION_LABELS}
            value={getCountryLabel(formData.country_code)}
            searchable
            onChange={handleCountryChange}
            placeholder="Select country"
          />
        </FormItem>

        <FormItem
          label="Pincode"
          name="pincode"
          error={errors.pincode}
          required={isIndia}
        >
          <input
            id="form-pincode"
            name="pincode"
            data-testid="pincode"
            value={formData.pincode}
            onChange={handleChange}
            maxLength={isIndia ? 6 : undefined}
            placeholder={
              isIndia ? "6 digits mandatory for India" : "Enter pincode"
            }
            className={`form-input ${errors.pincode ? "input-error" : ""}`}
          />
        </FormItem>

        <FormItem
          label="Mobile"
          name="mobile"
          error={errors.mobile}
          required
        >
          <div className="phone-field">
            <div className="phone-code-dropdown">
              <Dropdown
                name="phone_country_code"
                options={DIAL_OPTION_LABELS}
                value={getDialLabel(formData.phone_country_code)}
                displayValue={
                  formData.phone_country_code
                    ? `+${getDialCode(formData.phone_country_code)}`
                    : ""
                }
                searchable
                onChange={handlePhoneCountryChange}
                placeholder="Code"
              />
            </div>
            <input
              id="form-mobile"
              name="mobile"
              data-testid="mobile"
              inputMode="numeric"
              value={formData.mobile}
              onChange={handleChange}
              placeholder="Mobile number"
              className={`form-input ${errors.mobile ? "input-error" : ""}`}
            />
          </div>
        </FormItem>

        <FormItem
          label="Industry"
          name="industry"
          value={formData.industry}
          onChange={handleChange}
          error={errors.industry}
          required
        />

        <FormItem
          label="Business Type"
          name="business_type"
          error={errors.business_type}
          required
        >
          <Dropdown
            name="business_type"
            options={BUSINESS_TYPE_OPTIONS}
            value={formData.business_type}
            onChange={handleBusinessTypeChange}
            placeholder="Select business type"
          />
        </FormItem>

        <div className="password-field">
          <FormItem
            label="Password"
            name="password"
            type={showPassword ? "text" : "password"}
            value={formData.password}
            error={errors.password}
            onChange={handleChange}
            required={!isEdit}
          />
          <button
            type="button"
            className="password-toggle"
            aria-label={showPassword ? "Hide password" : "Show password"}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setShowPassword((visible) => !visible)}
          >
            {showPassword ? (
              <EyeOff className="text-primary" size={18} />
            ) : (
              <Eye className="text-primary" size={18} />
            )}
          </button>
        </div>

        <FormItem
          label="CRM Webhook"
          name="crm_webhook_url"
          className="form-item-full"
          value={formData.crm_webhook_url}
          onChange={handleChange}
          error={errors.crm_webhook_url}
        />

        <FormItem
          label="Primary Budget Buffer %"
          name="primary_budget_buffer_percent"
          type="number"
          error={errors.primary_budget_buffer_percent}
          value={formData.primary_budget_buffer_percent}
          onChange={handleChange}
        />

        <FormItem
          label="Secondary Budget Buffer %"
          name="secondary_budget_buffer_percent"
          type="number"
          error={errors.secondary_budget_buffer_percent}
          value={formData.secondary_budget_buffer_percent}
          onChange={handleChange}
        />


      </FormGrid>
      <FormItem
          label="About Us"
          name="about_us"
          type="textarea"
          value={formData.about_us}
          onChange={handleChange}
          error={errors.about_us}
        />

<FormItem label="Brochure" error={errors.brochure} >
  <div className="d-flex justify-center w-100 h-100 align-center">
        <FileUpload
          name="brochure"
          value={formData.brochure}
          onChange={handleChange}
          error={errors.brochure}
        />
        </div>
      </FormItem>
 

      <FormActions
        isLoading={isLoading}
        onCancel={handleCancel}
        saveText="Save Agent"
      />
    </form>
  );
}

/* ✅ Prop Validation */
AgentForm.propTypes = {
  initialData: PropTypes.shape({
    name: PropTypes.string,
    email: PropTypes.string,
    mobile: PropTypes.string,
    mobile_country_code: PropTypes.string,
    country_code: PropTypes.string,
    country: PropTypes.string,
    pincode: PropTypes.string,
    industry: PropTypes.string,
    business_type: PropTypes.string,
    crm_webhook_url: PropTypes.string,
    primary_budget_buffer_percent: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]),
    secondary_budget_buffer_percent: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]),
  }),

  onSubmit: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  onClose: PropTypes.func,
};